// Parses the trial-overview agent's summary markdown, whose structure is a
// contract with the biomind trial_overview knowledge pack: an intro line, a
// "**Key figures:**" line of `·`-separated stats, then ### sections.

export type OverviewSectionTone = "neutral" | "positive" | "attention";

export interface OverviewSection {
  heading: string;
  body: string;
  tone: OverviewSectionTone;
}

export interface ParsedTrialOverview {
  intro: string;
  keyFigures: string[];
  sections: OverviewSection[];
}

const KEY_FIGURES_PATTERN = /^\s*\*\*key figures:\*\*\s*(.+)$/i;
const HEADING_PATTERN = /^###\s+(.+)$/;

function sectionTone(heading: string): OverviewSectionTone {
  if (/going well/i.test(heading)) return "positive";
  if (/attention/i.test(heading)) return "attention";
  return "neutral";
}

export function parseTrialOverview(
  summary: string,
): ParsedTrialOverview | null {
  const lines = summary.split("\n");
  if (!lines.some((line) => HEADING_PATTERN.test(line))) return null;

  const introLines: string[] = [];
  let keyFigures: string[] = [];
  const sections: OverviewSection[] = [];
  let current: OverviewSection | null = null;

  for (const line of lines) {
    const headingMatch = line.match(HEADING_PATTERN);
    if (headingMatch) {
      if (current) sections.push({ ...current, body: current.body.trim() });
      const heading = headingMatch[1].trim();
      current = { heading, body: "", tone: sectionTone(heading) };
      continue;
    }
    if (current) {
      current.body += `${line}\n`;
      continue;
    }
    const figuresMatch = line.match(KEY_FIGURES_PATTERN);
    if (figuresMatch) {
      keyFigures = figuresMatch[1]
        .split("·")
        .map((figure) => figure.trim())
        .filter(Boolean);
      continue;
    }
    introLines.push(line);
  }
  if (current) sections.push({ ...current, body: current.body.trim() });

  return { intro: introLines.join("\n").trim(), keyFigures, sections };
}
